import { useMemo, useState } from 'react';
import type { BurndownPoint } from '../../hooks/useSprintTelemetry';
import type { TelemetryEvent } from '../../api/telemetry';
import type { SprintEpic, IssueExecution } from '../../api/sprint';

interface BurndownChartProps {
  data: BurndownPoint[];
  totalStories: number;
  startTime?: number;
  className?: string;
  telemetryEvents?: TelemetryEvent[];
  epics?: SprintEpic[];
  issueExecutions?: IssueExecution[];
}

const CHART_HEIGHT = 200;
const PAD_X = 44;
const PAD_Y = 24;
const PAD_TOP = 16;
const PAD_RIGHT = 16;

function fmtTime(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
}

function fmtDuration(ms: number): string {
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

const MARKER_CONFIG: Record<string, { fill: string; label: string; shape?: 'circle' | 'diamond' | 'triangle' | 'x' }> = {
  sprite_spawned:            { fill: '#D97F6F', label: 'Sprite Up' },
  verification_completed:    { fill: '#22c55e', label: 'Verified' },
  sprite_destroyed:          { fill: '#a3a3a3', label: 'Sprite Down' },
  commit_made:               { fill: '#3b82f6', label: 'Commit' },
  build_completed:           { fill: '#22c55e', label: 'Build Done', shape: 'diamond' },
  test_started:              { fill: '#3b82f6', label: 'E2E Tests', shape: 'triangle' },
  repair_started:            { fill: '#eab308', label: 'Repair', shape: 'circle' },
  sprite_lint_passed:        { fill: '#52A560', label: 'Lint Passed', shape: 'circle' },
  sprite_lint_failed:        { fill: '#C0392B', label: 'Lint Failed', shape: 'x' },
  sprite_typecheck_passed:   { fill: '#52A560', label: 'Typecheck Passed', shape: 'circle' },
  sprite_typecheck_failed:   { fill: '#C0392B', label: 'Typecheck Failed', shape: 'x' },
};

export default function BurndownChart({ data, totalStories, startTime, className, telemetryEvents = [], epics = [], issueExecutions = [] }: BurndownChartProps) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  const { width, innerW, innerH, xScale, yScale, idealPath, actualPath, fillPath, completedPath, completedFillPath, nowX, ticks } = useMemo(() => {
    const w = 800;
    const innerW = w - PAD_X - PAD_RIGHT;
    const innerH = CHART_HEIGHT - PAD_TOP - PAD_Y;

    if (data.length < 2 || totalStories === 0) {
      return { width: w, innerW, innerH, xScale: () => PAD_X, yScale: () => PAD_TOP, idealPath: '', actualPath: '', fillPath: '', completedPath: '', completedFillPath: '', nowX: PAD_X, ticks: [] };
    }

    const minT = startTime || data[0].timestamp;
    const lastPtTs = data[data.length - 1].timestamp;
    const maxT = data[data.length - 1].remaining === 0
      ? lastPtTs + (lastPtTs - minT) * 0.1
      : Math.max(lastPtTs, Date.now());
    const range = maxT - minT || 1;

    const xScale = (t: number) => PAD_X + ((t - minT) / range) * innerW;
    const yScale = (v: number) => PAD_TOP + innerH - (v / totalStories) * innerH;

    const idealPath = `M ${xScale(minT)},${yScale(totalStories)} L ${xScale(maxT)},${yScale(0)}`;

    // Remaining line (stepped)
    let actualPath = `M ${xScale(data[0].timestamp)},${yScale(data[0].remaining)}`;
    for (let i = 1; i < data.length; i++) {
      const prev = data[i - 1];
      const curr = data[i];
      actualPath += ` L ${xScale(curr.timestamp)},${yScale(prev.remaining)}`;
      actualPath += ` L ${xScale(curr.timestamp)},${yScale(curr.remaining)}`;
    }

    const lastPt = data[data.length - 1];
    const fillPath = `${actualPath} L ${xScale(lastPt.timestamp)},${yScale(0)} L ${xScale(data[0].timestamp)},${yScale(0)} Z`;

    // Completed line (stepped, cumulative successes)
    let completedPath = `M ${xScale(data[0].timestamp)},${yScale(data[0].completed)}`;
    for (let i = 1; i < data.length; i++) {
      const prev = data[i - 1];
      const curr = data[i];
      completedPath += ` L ${xScale(curr.timestamp)},${yScale(prev.completed)}`;
      completedPath += ` L ${xScale(curr.timestamp)},${yScale(curr.completed)}`;
    }

    const completedFillPath = `${completedPath} L ${xScale(lastPt.timestamp)},${yScale(0)} L ${xScale(data[0].timestamp)},${yScale(0)} Z`;

    const nowX = xScale(Date.now());

    const tickCount = 6;
    const ticks = Array.from({ length: tickCount }, (_, i) => {
      const t = minT + (range * i) / (tickCount - 1);
      return { x: xScale(t), label: fmtTime(t) };
    });

    return { width: w, innerW, innerH, xScale, yScale, idealPath, actualPath, fillPath, completedPath, completedFillPath, nowX, ticks };
  }, [data, totalStories, startTime]);

  // Lifecycle markers from telemetry events
  const lifecycleMarkers = useMemo(() => {
    if (data.length < 2 || totalStories === 0) return [];
    return telemetryEvents
      .filter((e) => MARKER_CONFIG[e.eventType])
      .map((e) => {
        const cfg = MARKER_CONFIG[e.eventType];
        const ts = new Date(e.timestamp).getTime();
        const isFailed = e.eventType === 'verification_completed' && (e.payload as Record<string, unknown>)?.passed === false;
        return {
          id: e.id,
          x: xScale(ts),
          fill: isFailed ? '#ef4444' : cfg.fill,
          label: isFailed ? 'Failed' : cfg.label,
          shape: cfg.shape || 'circle',
        };
      });
  }, [telemetryEvents, xScale, data.length, totalStories]);

  // Epic completion markers — green diamonds when all stories in an epic have status==='success'
  const epicMarkers = useMemo(() => {
    if (data.length < 2 || totalStories === 0 || epics.length === 0 || issueExecutions.length === 0) return [];
    const markers: Array<{ id: string; x: number; label: string }> = [];

    for (const epic of epics) {
      const epicExecs = issueExecutions.filter(e => e.epicId === epic.epicId);
      if (epicExecs.length === 0) continue;
      const allSuccess = epicExecs.every(e => e.status === 'success');
      if (!allSuccess) continue;

      // Find the latest completedAt among the epic's stories
      const latestTs = Math.max(...epicExecs.map(e => e.completedAt ? new Date(e.completedAt).getTime() : 0));
      if (latestTs === 0) continue;

      markers.push({
        id: `epic-${epic.epicId}`,
        x: xScale(latestTs),
        label: `Epic complete: ${epic.epicId}`,
      });
    }
    return markers;
  }, [epics, issueExecutions, xScale, data.length, totalStories]);

  if (data.length < 2 || totalStories === 0) {
    return (
      <div className={`bg-white rounded-lg border border-neutral-200 shadow-warm-sm p-6 ${className || ''}`}>
        <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400 mb-3">Burndown</p>
        <div className="h-[200px] flex items-center justify-center text-neutral-400 text-sm">
          No execution data yet
        </div>
      </div>
    );
  }

  const yTicks = Array.from({ length: Math.min(totalStories + 1, 6) }, (_, i) => {
    const count = Math.round((totalStories * i) / Math.min(totalStories, 5));
    return { y: yScale(count), label: `${count}` };
  });

  return (
    <div className={`bg-white rounded-lg border border-neutral-200 shadow-warm-sm p-4 ${className || ''}`}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">Burndown</p>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-0.5 bg-[#D97F6F] rounded-full" />
            <span className="text-[9px] text-neutral-500">Remaining</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-0.5 bg-[#52A560] rounded-full" />
            <span className="text-[9px] text-neutral-500">Completed</span>
          </div>
        </div>
      </div>
      <svg
        viewBox={`0 0 ${width} ${CHART_HEIGHT}`}
        className="w-full"
        preserveAspectRatio="xMidYMid meet"
        onMouseLeave={() => setHoveredIdx(null)}
      >
        <defs>
          <linearGradient id="burndown-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#D97F6F" stopOpacity={0.15} />
            <stop offset="100%" stopColor="#D97F6F" stopOpacity={0.02} />
          </linearGradient>
          <linearGradient id="completed-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#52A560" stopOpacity={0.12} />
            <stop offset="100%" stopColor="#52A560" stopOpacity={0.02} />
          </linearGradient>
        </defs>

        {/* Grid lines */}
        {yTicks.map((tick) => (
          <line key={tick.label} x1={PAD_X} x2={width - PAD_RIGHT} y1={tick.y} y2={tick.y} stroke="#e5e5e5" strokeWidth={0.5} />
        ))}

        {/* Y-axis labels */}
        {yTicks.map((tick) => (
          <text key={`y-${tick.label}`} x={PAD_X - 8} y={tick.y + 3} textAnchor="end" className="text-[9px] fill-neutral-400 font-mono">
            {tick.label}
          </text>
        ))}

        {/* X-axis labels */}
        {ticks.map((tick) => (
          <text key={tick.label} x={tick.x} y={CHART_HEIGHT - 4} textAnchor="middle" className="text-[8px] fill-neutral-400 font-mono">
            {tick.label}
          </text>
        ))}

        {/* Ideal line (dashed) */}
        <path d={idealPath} fill="none" stroke="#a3a3a3" strokeWidth={1} strokeDasharray="4 3" opacity={0.5} />

        {/* Remaining fill area */}
        <path d={fillPath} fill="url(#burndown-fill)" />

        {/* Remaining stepped line (coral) */}
        <path d={actualPath} fill="none" stroke="#D97F6F" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />

        {/* Completed fill area */}
        <path d={completedFillPath} fill="url(#completed-fill)" />

        {/* Completed stepped line (green) */}
        <path d={completedPath} fill="none" stroke="#52A560" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />

        {/* Now marker */}
        <line x1={nowX} x2={nowX} y1={PAD_TOP} y2={CHART_HEIGHT - PAD_Y} stroke="#D97F6F" strokeWidth={1} strokeDasharray="3 2" opacity={0.6} />
        <text x={nowX} y={PAD_TOP - 4} textAnchor="middle" className="text-[8px] fill-coral-500 font-semibold">
          NOW
        </text>

        {/* Data points — color-coded by status */}
        {data.map((pt, i) => {
          if (i === 0) return null;
          const cx = xScale(pt.timestamp);
          const cy = yScale(pt.remaining);
          const dotColor = pt.status === 'success' ? '#52A560' : pt.status === 'failure' ? '#C0392B' : '#D97F6F';
          return (
            <g key={i}>
              <circle cx={cx} cy={cy} r={16} fill="transparent" onMouseEnter={() => setHoveredIdx(i)} />
              <circle cx={cx} cy={cy} r={hoveredIdx === i ? 6 : 4} fill={dotColor} stroke="white" strokeWidth={1.5} />
              {pt.issueId && i > 0 && (cy - yScale(data[i - 1].remaining)) >= 15 && (
                <text x={cx + 8} y={cy + 3} className="text-[7px] fill-neutral-500 font-mono">{pt.issueId}</text>
              )}
            </g>
          );
        })}

        {/* Epic completion diamond markers */}
        {epicMarkers.map((m) => {
          const cy = yScale(0) - 8;
          return (
            <g key={m.id}>
              <rect x={m.x - 5} y={cy - 5} width={10} height={10} fill="#52A560" stroke="white" strokeWidth={1.5} transform={`rotate(45 ${m.x} ${cy})`} />
              <title>{m.label}</title>
            </g>
          );
        })}

        {/* Lifecycle markers along x-axis */}
        {lifecycleMarkers.map((m) => {
          const my = CHART_HEIGHT - PAD_Y + 8;
          return (
            <g key={m.id}>
              {m.shape === 'diamond' ? (
                <rect x={m.x - 4.5} y={my - 4.5} width={9} height={9} fill={m.fill} stroke="white" strokeWidth={1} transform={`rotate(45 ${m.x} ${my})`} />
              ) : m.shape === 'triangle' ? (
                <polygon points={`${m.x},${my - 6} ${m.x - 5},${my + 3.5} ${m.x + 5},${my + 3.5}`} fill={m.fill} stroke="white" strokeWidth={1} />
              ) : m.shape === 'x' ? (
                <g>
                  <line x1={m.x - 3} y1={my - 3} x2={m.x + 3} y2={my + 3} stroke={m.fill} strokeWidth={2} strokeLinecap="round" />
                  <line x1={m.x + 3} y1={my - 3} x2={m.x - 3} y2={my + 3} stroke={m.fill} strokeWidth={2} strokeLinecap="round" />
                </g>
              ) : (
                <circle cx={m.x} cy={my} r={m.shape === 'circle' ? 3.5 : 4.5} fill={m.fill} stroke="white" strokeWidth={1} />
              )}
              <title>{m.label}</title>
            </g>
          );
        })}

        {/* Hover tooltip */}
        {hoveredIdx !== null && data[hoveredIdx] && (() => {
          const pt = data[hoveredIdx];
          const cx = xScale(pt.timestamp);
          const cy = yScale(pt.remaining);
          const elapsed = pt.timestamp - (startTime || data[0].timestamp);
          const label = pt.issueId
            ? `${pt.issueId}${pt.issueTitle ? ` · ${pt.issueTitle.slice(0, 30)}` : ''}`
            : `${pt.remaining} remaining`;
          return (
            <g>
              <rect x={cx - 80} y={cy - 32} width={160} height={24} rx={4} fill="#1a1a1a" opacity={0.9} />
              <text x={cx} y={cy - 22} textAnchor="middle" className="text-[8px] fill-white font-mono">
                {label}
              </text>
              <text x={cx} y={cy - 13} textAnchor="middle" className="text-[7px] fill-neutral-400 font-mono">
                {fmtTime(pt.timestamp)} · +{fmtDuration(elapsed)}
              </text>
            </g>
          );
        })()}
      </svg>
    </div>
  );
}
